/**
 * MonthlyAdjustmentEditor
 *
 * Modal for Admin/CEO/Executive users to move dollars (or units) between the months of a
 * saved projection row without changing the GM's annual total. One tab per adjustable row of
 * the department (vehicle departments: Front Gross / F&I / Units). Save is enabled only when
 * the 12 months balance to the annual; the backend re-validates and rewrites the derived
 * Total Gross / Operating Profit records (see docs/features/projections.md).
 */

import React, { useEffect, useMemo, useState } from 'react';
import { MONTH_NAMES } from '../../types';
import { ProjectionCalculatedRecord } from '../../types/projections';
import { adjustProjectionMonths } from '../../services/projectionsApi';
import {
  deltasFrom,
  fromMonthArray,
  remainingToAllocate,
  sumValues,
  toMonthArray,
  validateDraft,
} from '../../utils/monthlyAdjustmentMath';

export interface AdjustableRow {
  metric: string;
  label: string;
  record: ProjectionCalculatedRecord;
}

interface MonthlyAdjustmentEditorProps {
  locationId: number;
  targetYear: number;
  department: string;
  departmentLabel: string;
  rows: AdjustableRow[];
  initialMetric?: string;
  formatCurrency: (value: number) => string;
  onClose: () => void;
  /** Called after a successful save or reset; the page reloads projections */
  onSaved: () => Promise<void> | void;
}

const formatDelta = (value: number, isUnits: boolean, formatCurrency: (v: number) => string) => {
  if (value === 0) return '';
  const abs = isUnits ? Math.abs(value).toLocaleString() : formatCurrency(Math.abs(value));
  return `${value > 0 ? '+' : '−'}${abs}`;
};

export const MonthlyAdjustmentEditor: React.FC<MonthlyAdjustmentEditorProps> = ({
  locationId, targetYear, department, departmentLabel, rows, initialMetric, formatCurrency, onClose, onSaved,
}) => {
  const [activeMetric, setActiveMetric] = useState(initialMetric && rows.some(r => r.metric === initialMetric) ? initialMetric : rows[0]?.metric);
  const active = rows.find(r => r.metric === activeMetric) || rows[0];
  const isUnits = active?.metric === 'UNITS';

  const base = useMemo(() => toMonthArray(active?.record.manualAdjustment?.baseDistribution ?? active?.record.monthlyDistribution), [active]);
  const current = useMemo(() => toMonthArray(active?.record.monthlyDistribution), [active]);
  const [draft, setDraft] = useState<string[]>(() => current.map(v => String(v)));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Switching tabs starts a fresh draft from that row's current months
  useEffect(() => {
    setDraft(current.map(v => String(v)));
    setError(null);
  }, [current]);

  const values = draft.map(v => (v.trim() === '' ? 0 : Number(v)));
  const annual = active?.record.annualTotal ?? 0;
  // Balance target: the base spread's own total (= annual up to seasonality rounding), so the
  // deltas sum to exactly 0 - same rule as the backend
  const target = sumValues(base);
  const remaining = remainingToAllocate(values, target);
  const validation = active ? validateDraft(department, active.metric, values, target) : null;
  const deltas = deltasFrom(values, base);
  const changed = values.some((v, i) => v !== current[i]);
  const fmt = (v: number) => (isUnits ? v.toLocaleString() : formatCurrency(v));

  // Vehicle departments: the read-only Total Gross line = front + F&I using this draft
  const totalGrossPreview = useMemo(() => {
    if (!active || isUnits || !rows.some(r => r.metric === 'GROSS') || !rows.some(r => r.metric === 'FI_INCOME')) return null;
    const other = rows.find(r => r.metric === (active.metric === 'GROSS' ? 'FI_INCOME' : 'GROSS'))!;
    const otherMonths = toMonthArray(other.record.monthlyDistribution);
    return values.map((v, i) => v + otherMonths[i]);
  }, [active, isUnits, rows, values]);

  const setMonth = (i: number, text: string) => {
    setDraft(prev => prev.map((v, j) => (j === i ? text : v)));
    setError(null);
  };

  const submit = async (reset: boolean) => {
    if (!active) return;
    setSaving(true);
    setError(null);
    try {
      const response = await adjustProjectionMonths({
        locationId, targetYear, department, metric: active.metric,
        ...(reset ? { reset: true } : { monthlyValues: fromMonthArray(values) }),
        expectedAdjustedAt: active.record.manualAdjustment?.adjustedAt ?? null,
      });
      if (!response.success) {
        setError(response.error || 'Failed to adjust months');
        return;
      }
      await onSaved();
      onClose();
    } catch (err: any) {
      setError(err?.message || 'Failed to adjust months');
    } finally {
      setSaving(false);
    }
  };

  if (!active) return null;
  const adj = active.record.manualAdjustment;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true" aria-labelledby="monthly-adjustment-title">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="px-5 py-3 border-b border-gray-200 flex items-start justify-between">
          <div>
            <h3 id="monthly-adjustment-title" className="text-lg font-semibold text-gray-900">
              Adjust months: {departmentLabel} {targetYear}
            </h3>
            <p className="text-sm text-gray-500">
              Move amounts between months. The annual total stays at <strong>{fmt(annual)}</strong>.
              {adj && !adj.derived && (
                <> Adjusted by {adj.adjustedByName} on {new Date(adj.adjustedAt).toLocaleDateString('en-US')}.</>
              )}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none" aria-label="Close">&times;</button>
        </div>

        {rows.length > 1 && (
          <div className="px-5 pt-3 flex gap-2">
            {rows.map(r => (
              <button
                key={r.metric}
                onClick={() => setActiveMetric(r.metric)}
                className={`px-3 py-1.5 text-sm font-medium rounded-md ${r.metric === active.metric ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
              >
                {r.label}{r.record.manualAdjustment && !r.record.manualAdjustment.derived ? ' •' : ''}
              </button>
            ))}
          </div>
        )}

        <div className="px-5 py-3 overflow-y-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-gray-500 border-b border-gray-200">
                <th className="text-left py-1">Month</th>
                <th className="text-right py-1">Seasonality</th>
                <th className="text-right py-1">Current</th>
                <th className="text-right py-1">New</th>
                <th className="text-right py-1">Change vs seasonality</th>
              </tr>
            </thead>
            <tbody>
              {MONTH_NAMES.map((name, i) => (
                <tr key={name} className="border-b border-gray-100">
                  <td className="py-1 font-medium text-gray-800">{name}</td>
                  <td className="py-1 text-right text-gray-500">{fmt(base[i])}</td>
                  <td className="py-1 text-right text-gray-700">{fmt(current[i])}</td>
                  <td className="py-1 text-right">
                    <input
                      type="number"
                      inputMode="numeric"
                      step={1}
                      min={0}
                      value={draft[i]}
                      onChange={e => setMonth(i, e.target.value)}
                      aria-label={`${name} ${active.label}`}
                      className={`w-32 text-right border rounded px-2 py-1 ${values[i] < 0 || !Number.isInteger(values[i]) ? 'border-red-400' : 'border-gray-300'}`}
                    />
                  </td>
                  <td className={`py-1 text-right ${deltas[i] > 0 ? 'text-green-700' : deltas[i] < 0 ? 'text-red-700' : 'text-gray-400'}`}>
                    {formatDelta(deltas[i], isUnits, formatCurrency)}
                  </td>
                </tr>
              ))}
              {totalGrossPreview && (
                <tr className="text-xs text-gray-500">
                  <td className="py-1 text-right" colSpan={5}>
                    Total Gross (front + F&I) with this draft: {formatCurrency(sumValues(totalGrossPreview))} annual
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="px-5 py-3 border-t border-gray-200 flex items-center justify-between gap-4">
          <div className="text-sm">
            <span className="text-gray-500">Total {fmt(target)} · Allocated {fmt(sumValues(values))} · </span>
            <span className={remaining === 0 ? 'text-green-700 font-semibold' : 'text-amber-700 font-semibold'}>
              Remaining {isUnits ? remaining.toLocaleString() : formatDelta(remaining, false, formatCurrency) || formatCurrency(0)}
            </span>
            {validation && validation.code !== 'SUM_MISMATCH' && (
              <div className="text-red-700 text-xs mt-1">{validation.message}</div>
            )}
            {error && <div className="text-red-700 text-xs mt-1">{error}</div>}
          </div>
          <div className="flex gap-2">
            {adj && !adj.derived && (
              <button
                onClick={() => submit(true)}
                disabled={saving}
                className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
              >
                Reset to seasonality
              </button>
            )}
            <button onClick={onClose} disabled={saving} className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
              Cancel
            </button>
            <button
              onClick={() => submit(false)}
              disabled={saving || !!validation || !changed}
              className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save adjustment'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
